import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { fr } from 'date-fns/locale';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  getMonth,
  getYear,
  isWithinInterval,
  getDay
} from 'date-fns';
import Holidays from 'date-holidays';
import { CalendarToday as CalendarIcon, ViewWeek as ViewWeekIcon, CalendarViewMonth as CalendarViewMonthIcon } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Fonction pour générer les jours fériés dynamiquement
const generateHolidays = (startYear, endYear) => {
  try {
    const hd = new Holidays('FR');
    
    // Récupérer les jours fériés pour l'année scolaire (juillet à août)
    const startYearHolidays = hd.getHolidays(startYear);
    const endYearHolidays = hd.getHolidays(endYear);
    
    // Convertir les dates en format yyyy-MM-dd pour faciliter la comparaison
    const formattedStartYearHolidays = startYearHolidays.map(h => ({
      ...h,
      date: format(new Date(h.date), 'yyyy-MM-dd')
    }));
    
    const formattedEndYearHolidays = endYearHolidays.map(h => ({
      ...h,
      date: format(new Date(h.date), 'yyyy-MM-dd')
    }));
    
    // Filtrer pour ne garder que les jours fériés de l'année scolaire
    // (de juillet de l'année de début à août de l'année de fin)
    const filteredHolidays = [
      ...formattedStartYearHolidays.filter(h => new Date(h.date).getMonth() >= 6), // Juillet à décembre
      ...formattedEndYearHolidays.filter(h => new Date(h.date).getMonth() <= 7)  // Janvier à août
    ];
    
    // Trier les jours fériés par date
    filteredHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    
    return filteredHolidays;
  } catch (error) {
    console.error('Erreur lors de la génération des jours fériés:', error);
    return [];
  }
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const Calendar = () => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [vacationPeriods, setVacationPeriods] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' ou 'year'
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [showHolidaysInCalendar, setShowHolidaysInCalendar] = useState(true);
  
  // Générer les mois de l'année scolaire en fonction des paramètres
  const months = [];
  
  // Récupérer les mois et années de début et de fin depuis les paramètres de l'établissement
  const moisDebut = establishmentInfo?.moisDebut !== undefined ? establishmentInfo.moisDebut : 6; // Juillet par défaut
  const moisFin = establishmentInfo?.moisFin !== undefined ? establishmentInfo.moisFin : 7; // Août par défaut
  const anneeDebut = establishmentInfo?.anneeDebut !== undefined ? establishmentInfo.anneeDebut : new Date().getFullYear();
  const anneeFin = establishmentInfo?.anneeFin !== undefined ? establishmentInfo.anneeFin : new Date().getFullYear();
  
  // Date de début et de fin
  const dateDebut = new Date(anneeDebut, moisDebut, 1);
  const dateFin = new Date(anneeFin, moisFin, 1);
  
  // Calculer le nombre de mois entre les deux dates
  const diffAnnees = anneeFin - anneeDebut;
  const diffMois = moisFin - moisDebut;
  const nombreMois = diffAnnees * 12 + diffMois + 1; // +1 pour inclure le mois de fin
  
  // Générer tous les mois entre la date de début et la date de fin
  for (let i = 0; i < nombreMois; i++) {
    // Commencer par le mois de début de l'année scolaire
    let year = anneeDebut;
    let month = moisDebut + i;
    
    // Si le mois dépasse décembre, ajuster l'année et le mois
    if (month > 11) {
      const anneesSupplement = Math.floor(month / 12);
      month = month % 12;
      year = year + anneesSupplement;
    }
    
    const monthDate = new Date(year, month, 1);
    months.push(monthDate);
  }

  // Fonction pour récupérer les périodes de vacances
  const fetchVacationPeriods = async () => {
    try {
      setLoading(true);
      
      if (!userData?.establishmentId) {
        setLoading(false);
        return;
      }

      const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
      const snapshot = await getDocs(vacancesRef);
      
      const periods = snapshot.docs.map(doc => {
        const data = doc.data();
        
        if (!data.debut || !data.fin) {
          return null;
        }

        const period = {
          id: doc.id,
          ...data,
          start: data.debut.toDate(),
          end: data.fin.toDate(),
          name: data.nom,
          type: data.type
        };
        
        return period;
      }).filter(Boolean);

      setVacationPeriods(periods);
      setCurrentDate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des périodes de vacances:', err);
      setError('Erreur lors du chargement du calendrier');
      setLoading(false);
    }
  };

  // Récupérer les informations de l'établissement
  const fetchEstablishmentInfo = async () => {
    try {
      if (!userData?.establishmentId) {
        return;
      }

      const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
      if (establishmentDoc.exists()) {
        const data = establishmentDoc.data();
        setEstablishmentInfo(data);
        
        // Récupérer la préférence d'affichage des jours fériés
        setShowHolidaysInCalendar(data.showHolidaysInCalendar !== undefined ? data.showHolidaysInCalendar : true);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des informations de l\'établissement:', err);
    }
  };

  // Fonction pour charger les jours fériés
  const loadHolidays = () => {
    if (!establishmentInfo) return;
    
    const startYear = establishmentInfo.anneeDebut || new Date().getFullYear();
    const endYear = establishmentInfo.anneeFin || startYear + 1;
    
    try {
      const holidaysList = generateHolidays(startYear, endYear);
      setHolidays(holidaysList);
    } catch (error) {
      console.error('Erreur lors du chargement des jours fériés:', error);
    }
  };

  // Vérifier si une date est un jour férié
  const isHoliday = (date) => {
    if (!showHolidaysInCalendar || !holidays || holidays.length === 0) return null;
    
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    return holidays.find(holiday => holiday.date === formattedDate);
  };

  // Vérifier si une date est pendant les vacances
  const isVacation = (date) => {
    if (!date || !vacationPeriods || vacationPeriods.length === 0) return false;
    
    return vacationPeriods.some(period => {
      try {
        if (!period.start || !period.end) return false;
        return isWithinInterval(date, { start: period.start, end: period.end });
      } catch (error) {
        console.error('Erreur dans isVacation:', error);
        return false;
      }
    });
  };

  // Obtenir la couleur de fond en fonction du type de vacances
  const getVacationColor = (type) => {
    switch (type) {
      case 'printemps': return '#a5d6a7';
      case 'ete': return '#fff59d';
      case 'automne': return '#ffcc80';
      case 'hiver': return '#90caf9';
      default: return '#e0e0e0';
    }
  };

  // Obtenir la couleur de bordure en fonction du type de vacances
  const getVacationBorderColor = (type) => {
    switch (type) {
      case 'printemps': return '#2e7d32';
      case 'ete': return '#fbc02d';
      case 'automne': return '#f57c00';
      case 'hiver': return '#1976d2';
      default: return '#9e9e9e';
    }
  };

  // Chargement des données au montage du composant
  useEffect(() => {
    const loadData = async () => {
      await fetchVacationPeriods();
      await fetchEstablishmentInfo();
    };
    
    loadData();
  }, [userData]);
  
  // Charger les jours fériés quand les informations de l'établissement changent
  useEffect(() => {
    if (establishmentInfo) {
      loadHolidays();
    }
  }, [establishmentInfo, showHolidaysInCalendar]);

  // Générer le calendrier pour un mois
  const renderMonth = (date) => {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Calculer le jour de la semaine du premier jour du mois (0 = dimanche, 1 = lundi, etc.)
    const startDay = getDay(monthStart);
    // Ajuster pour que la semaine commence le lundi (0 = lundi, 6 = dimanche)
    const daysToAdd = startDay === 0 ? 6 : startDay - 1;
    
    // Fonction pour générer la bordure des cellules
    const border = (theme) => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
    });

    return (
      <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
        <Typography variant="subtitle1" align="center" sx={{ mb: 1, fontWeight: 'bold' }}>
          {format(date, 'MMMM yyyy', { locale: fr })}
        </Typography>
        
        <Grid container spacing={0.5}>
          {/* En-têtes des jours de la semaine */}
          {DAYS.map((day, index) => (
            <Grid item xs={12/7} key={`header-${index}`}>
              <Box sx={{ 
                p: 0.5, 
                textAlign: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                {day}
              </Box>
            </Grid>
          ))}
          
          {/* Jours vides avant le début du mois */}
          {Array.from({ length: daysToAdd }).map((_, index) => (
            <Grid item xs={12/7} key={`empty-${index}`}>
              <Box sx={{ p: 0.5 }} />
            </Grid>
          ))}
          
          {/* Jours du mois */}
          {days.map((day, index) => {
            const isHolidayDay = isHoliday(day);
            const isVacationDay = isVacation(day);
            let vacationType = '';
            
            if (isVacationDay) {
              const vacationPeriod = vacationPeriods.find(period => 
                isWithinInterval(day, { start: period.start, end: period.end })
              );
              vacationType = vacationPeriod?.type || '';
            }
            
            return (
              <Grid item xs={12/7} key={`day-${index}`}>
                <Box sx={{ 
                  p: 0.5, 
                  textAlign: 'center',
                  bgcolor: isHolidayDay 
                    ? '#ffebee' 
                    : isVacationDay 
                      ? getVacationColor(vacationType)
                      : 'transparent',
                  border: isHolidayDay 
                    ? '1px solid #c62828' 
                    : isVacationDay 
                      ? `1px solid ${getVacationBorderColor(vacationType)}`
                      : '1px solid transparent',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  fontWeight: isHolidayDay ? 'bold' : 'normal',
                  color: isHolidayDay ? '#c62828' : 'text.primary',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {format(day, 'd')}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  if (loading || !currentDate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CalendarIcon sx={{ mr: 2 }} />
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Calendrier Scolaire
          </Typography>
          <Button
            variant="contained"
            startIcon={viewMode === 'month' ? <CalendarViewMonthIcon /> : <ViewWeekIcon />}
            onClick={() => setViewMode(viewMode === 'month' ? 'year' : 'month')}
            sx={{ ml: 2 }}
          >
            Vue {viewMode === 'month' ? 'Annuelle' : 'Mensuelle'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        <Paper sx={{ p: 3 }} id="calendar">
          {/* Titre avec nom de l'établissement */}
          <Typography variant="h5" component="h1" gutterBottom align="center">
            {establishmentInfo?.name || 'Calendrier Scolaire'}
          </Typography>
          
          {/* Sous-titre année scolaire */}
          <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
            Année scolaire {establishmentInfo?.anneeScolaire || `${new Date(anneeDebut, moisDebut, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} - ${new Date(anneeFin, moisFin, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`}
          </Typography>

          {/* Affichage du calendrier selon le mode */}
          {viewMode === 'month' ? (
            // Vue d'un seul mois
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
              {renderMonth(currentDate)}
            </Box>
          ) : (
            // Vue annuelle
            <Grid container spacing={2}>
              {months.map((month, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  {renderMonth(month)}
                </Grid>
              ))}
            </Grid>
          )}

          {/* Légende */}
          <Box sx={{ mt: 4, display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#ffebee', 
                borderRadius: 1,
                border: '1px solid #c62828'
              }} />
              <Typography variant="body2">Jours fériés</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#a5d6a7', 
                borderRadius: 1,
                border: '1px solid #2e7d32',
              }} />
              <Typography variant="body2">Vacances de printemps</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#fff59d', 
                borderRadius: 1,
                border: '1px solid #fbc02d',
              }} />
              <Typography variant="body2">Vacances d'été</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#ffcc80', 
                borderRadius: 1,
                border: '1px solid #f57c00',
              }} />
              <Typography variant="body2">Vacances d'automne</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                bgcolor: '#90caf9', 
                borderRadius: 1,
                border: '1px solid #1976d2',
              }} />
              <Typography variant="body2">Vacances d'hiver</Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default Calendar;
